import { Languages } from 'lucide-react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useUpdateProfile } from '@/api/profile'

interface Props {
  collapsed: boolean
  darkMode: boolean
  /** Icône + code de la langue courante, pour la barre d'outils du bas. */
  compact?: boolean
}

const SUPPORTED: Array<{ code: 'fr' | 'en'; label: string }> = [
  { code: 'fr', label: 'FR' },
  { code: 'en', label: 'EN' },
]

/**
 * Bouton de bascule rapide de langue, monté dans les sidebars (Dashboard +
 * Manager). Toggle entre FR et EN en un seul clic, sans avoir à ouvrir la
 * page Profil.
 *
 * Effets :
 *   - i18n.changeLanguage : bascule i18next, l'UI bascule immédiatement
 *   - localStorage `preferred_locale` : préférence pour les futures visites
 *     (cold start, URLs publiques /call?number=... etc.)
 *   - navigate : remplace le segment locale dans l'URL pour rester cohérent
 *   - updateProfile (fire-and-forget) : persiste la préférence côté backend
 *     pour que ce soit cross-device. Si l'API échoue, on tolère silencieusement
 *     — la session courante a déjà appliqué la langue.
 */
export function LanguageToggle({ collapsed, darkMode, compact = false }: Props) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const updateProfile = useUpdateProfile()

  const current = (i18n.language?.startsWith('en') ? 'en' : 'fr') as 'fr' | 'en'
  const next = current === 'fr' ? 'en' : 'fr'

  const handleToggle = () => {
    void i18n.changeLanguage(next)
    try {
      localStorage.setItem('preferred_locale', next)
    } catch {
      // ignore (private mode etc.)
    }
    const newPath = window.location.pathname.replace(
      /^\/(?:fr|en)(\/|$)/,
      `/${next}$1`,
    )
    navigate(newPath + window.location.search, { replace: true })

    // Persist côté backend pour cross-device. Fire-and-forget — on n'attend
    // pas la réponse, l'UX est déjà mise à jour. Si la mutation échoue, on
    // tolère silencieusement (toggle marche dans la session actuelle).
    updateProfile.mutate({ language: next })
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      title={t('sidebar.switchLanguage', 'Changer la langue')}
      aria-label={t('sidebar.switchLanguage', 'Changer la langue')}
      className={cn(
        'flex items-center h-7 rounded-md text-[13px] transition-colors',
        compact ? 'gap-1 px-2' : 'w-full',
        !compact && (collapsed ? 'justify-center px-2' : 'gap-2.5 px-3'),
        darkMode
          ? 'text-slate-300 hover:bg-slate-800 hover:text-white'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
      )}
    >
      <Languages className="h-4 w-4" />
      {compact && <span className="text-[11px] font-semibold uppercase">{current}</span>}
      {!collapsed && !compact && (
        <span className="flex items-center gap-1.5">
          {SUPPORTED.map((lang, idx) => (
            <span key={lang.code} className="flex items-center gap-1.5">
              <span
                className={cn(
                  'font-mono text-xs font-semibold tracking-wide transition-colors',
                  lang.code === current
                    ? darkMode
                      ? 'text-white'
                      : 'text-slate-900'
                    : darkMode
                      ? 'text-slate-600'
                      : 'text-slate-300',
                )}
              >
                {lang.label}
              </span>
              {idx === 0 && (
                <span
                  className={cn(
                    'text-xs',
                    darkMode ? 'text-slate-600' : 'text-slate-300',
                  )}
                >
                  /
                </span>
              )}
            </span>
          ))}
        </span>
      )}
    </button>
  )
}
